import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CLAIMABLE_MONTHS,
  convertToHome,
  findDuplicateInDraft,
  fmtDateTime,
  money,
  periodLabel,
  validatePeriod,
  type Claim,
  type ExpenseCategory,
} from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { SignatureMark, useMySignature } from '@/components/SignatureMark';
import { useCurrentUser } from '@/auth/AuthContext';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import {
  DateConfirmModal,
  matchesPeriod,
  type DateChoice,
  type PendingDateConfirm,
} from '@/components/claim-form/DateConfirmModal';
import { DraftTable } from '@/components/claim-form/DraftTable';
import {
  ReceiptDropzone,
  UploadQueue,
  type QueueItem,
  type QueueStatus,
} from '@/components/claim-form/ReceiptDropzone';
import {
  draftReducer,
  draftTotals,
  emptyDraft,
  makeRow,
  toWireRow,
  type DraftRow,
} from '@/components/claim-form/draft';
import {
  outstandingFindings,
  periodSelectorOptions,
  rowsForPeriod,
} from '@/components/claim-form/formLogic';
import {
  extractReceipts,
  prepareFile,
  uploadReceipt,
  type AmbiguousWireRow,
  type ExtractedWireRow,
} from '@/components/claim-form/receiptUpload';

interface ApprovalPreviewStep {
  role: string;
  userId: string;
  name: string;
}

interface ApprovalPreview {
  executiveRoute: boolean;
  steps: ApprovalPreviewStep[];
}

interface DuplicateCheckResult {
  duplicate: boolean;
  where?: string;
}

/** A confirmation still waiting on the requester, plus what to build if they confirm. */
interface PendingConfirm extends PendingDateConfirm {
  wire: AmbiguousWireRow;
  sourceName: string;
  imageKey: string;
}

/** Long enough that typing a receipt number does not fire a call per keystroke. */
const AUTO_RECHECK_DEBOUNCE_MS = 1200;
const DUPLICATE_CHECK_DEBOUNCE_MS = 400;

/**
 * Findings that must be remembered because the browser cannot work them out.
 *
 * `unreadable` is about the file itself, and `duplicate_elsewhere` needs the
 * whole claim corpus, which the browser must never hold. Every other kind is
 * re-derived on each render, so storing it would only let it go stale.
 */
const STICKY_ANOMALIES = new Set(['unreadable', 'duplicate_elsewhere']);

function rowFromWire(
  wire: ExtractedWireRow,
  sourceName: string,
  previewUrl: string | null,
  imageKey: string,
  dateOverride?: string,
): DraftRow {
  return makeRow({
    date: dateOverride ?? wire.date ?? '',
    receipt: wire.receipt ?? '',
    company: wire.company ?? '',
    tin: wire.tin ?? '',
    description: wire.description ?? '',
    // A converted row arrives with amount already in pesos and the original
    // beside it; a peso row arrives with the two the same.
    amount: wire.amount === undefined || wire.amount === 0 ? '' : String(wire.amount),
    currency: (wire.currency ?? 'PHP').toUpperCase(),
    originalAmount: wire.originalAmount === undefined || wire.originalAmount === null
      ? ''
      : String(wire.originalAmount),
    fxRate: wire.fxRate ?? null,
    vatStatus: wire.vatStatus === 'Yes' ? 'Yes' : 'No',
    printedNet: wire.printedNet ?? null,
    printedVat: wire.printedVat ?? null,
    imageKey: wire.imageKey ?? imageKey,
    previewUrl,
    sourceName,
  });
}

function without(record: Readonly<Record<string, string>>, key: string): Record<string, string> {
  const next = { ...record };
  delete next[key];
  return next;
}

export function ClaimFormPage(): ReactNode {
  const user = useCurrentUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams();
  const resubmitId = searchParams.get('resubmit') ?? routeParams.claimId ?? null;

  const [state, dispatch] = useReducer(draftReducer, undefined, () => emptyDraft());
  const [categories, setCategories] = useState<string[]>([]);
  const [preview, setPreview] = useState<ApprovalPreview | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [pendingConfirms, setPendingConfirms] = useState<PendingConfirm[]>([]);
  const [warnings, setWarnings] = useState<Record<string, string>>({});
  /** Finance's current rates, home-currency units per 1 unit of the key. */
  const [rates, setRates] = useState<Record<string, number>>({});
  const [rechecking, setRechecking] = useState(false);
  const mySignature = useMySignature();
  /**
   * What the last re-check found, so the panel can say what changed rather than
   * silently shrinking. `outstanding` is the key set at that moment; anything
   * that has since disappeared from the live findings was fixed.
   */
  const [lastCheck, setLastCheck] = useState<{ at: number; outstanding: string[] } | null>(null);
  const [disapprovalReason, setDisapprovalReason] = useState('');
  const [loadingClaim, setLoadingClaim] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Server-side draft this form is bound to, if any. Null until first save.
  const [draftId, setDraftId] = useState<string | null>(searchParams.get('draft'));
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);

  const queueSeq = useRef(0);
  const confirmSeq = useRef(0);
  const objectUrls = useRef<string[]>([]);
  const dupTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // File processing runs outside the render that started it, so it reads the
  // draft through refs rather than a captured (and by then stale) closure.
  const periodRef = useRef(state.period);
  const rowsRef = useRef<DraftRow[]>(state.rows);
  const editingRef = useRef<string | null>(state.editingClaimId);
  useEffect(() => {
    periodRef.current = state.period;
    rowsRef.current = state.rows;
    editingRef.current = state.editingClaimId;
  });

  useEffect(() => {
    const urls = objectUrls.current;
    const timers = dupTimers.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  /* ---------------- Reference data ---------------- */

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [cats, routing, fx] = await Promise.all([
          api.directory.categories<ExpenseCategory[]>(),
          api.directory.approvalPreview<ApprovalPreview>({}),
          api.fx.list<{ rates: Array<{ currency: string; rate: number }> }>(),
        ]);
        if (cancelled) return;
        setCategories(cats.map((c) => c.name));
        setPreview(routing);
        setRates(Object.fromEntries(fx.rates.map((r) => [r.currency, r.rate])));
      } catch (err) {
        if (!cancelled) toast(errorMessage(err), 'err');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  /* ---------------- Resubmit prefill ---------------- */

  useEffect(() => {
    if (!resubmitId) {
      dispatch({ type: 'reset', state: emptyDraft() });
      setDisapprovalReason('');
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoadingClaim(true);
    setLoadError(null);
    void (async () => {
      try {
        const claim = await api.claims.get<Claim>({ claimId: resubmitId });
        if (cancelled) return;
        if (claim.requesterId !== user.id) {
          setLoadError('You can only resubmit your own claim.');
          return;
        }
        if (claim.status !== 'Disapproved') {
          setLoadError(
            `Claim ${claim.id} is currently "${claim.status}" — only a disapproved claim can be resubmitted.`,
          );
          return;
        }
        dispatch({
          type: 'reset',
          state: {
            period: claim.period,
            editingClaimId: claim.id,
            rows: claim.rows.map((r) =>
              makeRow({
                date: r.date,
                receipt: r.receipt,
                company: r.company,
                tin: r.tin,
                description: r.description,
                amount: String(r.amount),
                // A resubmitted row keeps the currency it was originally read
                // in; the rate is re-applied on submit from whatever Finance
                // maintains then, so a stale rate cannot ride along.
                currency: r.currency ?? 'PHP',
                originalAmount: r.originalAmount === null || r.originalAmount === undefined
                  ? ''
                  : String(r.originalAmount),
                fxRate: r.fxRate ?? null,
                vatStatus: r.vatStatus,
                imageKey: r.imageKey,
                sourceName: r.sourceName ?? '',
              }),
            ),
          },
        });
        setDisapprovalReason(claim.disapprovalReason);
      } catch (err) {
        if (!cancelled) setLoadError(errorMessage(err));
      } finally {
        if (!cancelled) setLoadingClaim(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resubmitId, user.id]);

  /* ---------------- Row editing ---------------- */

  const onField = useCallback((id: string, patch: Partial<DraftRow>) => {
    dispatch({ type: 'updateRow', id, patch });
  }, []);

  const onDateCommit = useCallback(
    (id: string, value: string) => {
      if (!value) {
        dispatch({ type: 'updateRow', id, patch: { date: '' } });
        return;
      }
      // The value is always committed, even when it is out of period.
      //
      // Refusing to store it meant the field fought the person fixing it: the
      // input is controlled, so every rejected keystroke snapped it back to the
      // old date, and correcting a year one digit at a time was impossible —
      // 2024 → 2026 passes through 0002, 0020, 0202, each of them rejected.
      // The anomaly panel already reports an out-of-period date and blocks
      // submission, which is the right place for that to happen.
      dispatch({ type: 'updateRow', id, patch: { date: value } });
    },
    [toast],
  );

  const scheduleServerDuplicateCheck = useCallback((rowId: string, receiptNo: string) => {
    const timers = dupTimers.current;
    const existing = timers.get(rowId);
    if (existing !== undefined) clearTimeout(existing);

    const value = receiptNo.trim();
    if (!value) return;

    timers.set(
      rowId,
      setTimeout(() => {
        timers.delete(rowId);
        void (async () => {
          try {
            const editing = editingRef.current;
            const result = await api.claims.checkReceiptDuplicate<DuplicateCheckResult>({
              receiptNo: value,
              ...(editing ? { excludeClaimId: editing } : {}),
            });
            const message = `Receipt No. "${value}" is already used in ${result.where ?? 'another claim'}.`;
            setWarnings((prev) => (result.duplicate ? { ...prev, [rowId]: message } : without(prev, rowId)));
            // Also recorded on the row itself. The warnings map is presentation;
            // this is what stops the claim being submitted.
            dispatch({
              type: 'updateRow',
              id: rowId,
              patch: {
                serverAnomaly: result.duplicate ? { kind: 'duplicate_elsewhere', message } : null,
              },
            });
          } catch {
            // A duplicate check that cannot reach the server must not block
            // editing; submit re-runs the same check server-side anyway.
          }
        })();
      }, DUPLICATE_CHECK_DEBOUNCE_MS),
    );
  }, []);

  const onReceiptCommit = useCallback(
    (rowId: string, value: string) => {
      const dup = findDuplicateInDraft(rowsRef.current, value, rowId);
      if (dup) {
        const message = `Receipt No. "${value}" is already used in ${dup.where}.`;
        setWarnings((prev) => ({ ...prev, [rowId]: message }));
        toast(`${message} Please double-check this receipt.`, 'err');
        return;
      }
      setWarnings((prev) => without(prev, rowId));
      scheduleServerDuplicateCheck(rowId, value);
    },
    [scheduleServerDuplicateCheck, toast],
  );

  const onDeleteRow = useCallback((id: string) => {
    const timer = dupTimers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      dupTimers.current.delete(id);
    }
    setWarnings((prev) => without(prev, id));
    dispatch({ type: 'deleteRow', id });
  }, []);

  const onAddRow = useCallback(() => {
    dispatch({ type: 'addRows', rows: [makeRow()] });
  }, []);

  /* ---------------- Period ---------------- */

  /** Open periods, plus this claim's own period (disabled) if it has since closed. */
  const periodOptions = useMemo(() => periodSelectorOptions(state.period), [state.period]);

  const onPeriodChange = useCallback(
    (period: string) => {
      if (!period) return;
      const invalid = validatePeriod(period);
      if (invalid) {
        toast(invalid, 'err');
        return;
      }

      // Every existing row is re-checked against the new period; a receipt that
      // no longer belongs cannot silently ride along on the claim.
      const { kept, dropped } = rowsForPeriod(state.rows, period);

      dispatch({ type: 'setPeriod', period, rows: kept });
      if (dropped > 0) {
        toast(
          `${dropped} receipt${dropped > 1 ? 's' : ''} removed — the date${dropped > 1 ? 's' : ''} no longer match ${periodLabel(period)}.`,
          'err',
        );
      }
    },
    [state.rows, toast],
  );

  /* ---------------- Uploads ---------------- */

  const setQueueItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setQueue((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const processFile = useCallback(
    async (file: File): Promise<void> => {
      queueSeq.current += 1;
      const qid = `q${queueSeq.current}`;
      const period = periodRef.current;
      setQueue((prev) => [
        ...prev,
        { id: qid, name: file.name, status: 'working', message: 'preparing…' },
      ]);

      let previewUrl: string | null = null;
      let uploadedKey: string | null = null;

      try {
        const prepared = await prepareFile(file);
        if (prepared.contentType.startsWith('image/')) {
          previewUrl = URL.createObjectURL(prepared.blob);
          objectUrls.current.push(previewUrl);
        }

        setQueueItem(qid, { message: 'uploading…' });
        uploadedKey = await uploadReceipt(prepared, period);

        setQueueItem(qid, { message: 'reading the receipt…' });
        const result = await extractReceipts(uploadedKey, period);

        const fresh: DraftRow[] = [];
        let flaggedCount = 0;
        for (const wire of result.accepted) {
          const receiptNo = (wire.receipt ?? '').trim();
          // The server checks duplicates across the system; this catches the
          // case it cannot see — the same receipt twice in the open draft.
          const dup = receiptNo
            ? findDuplicateInDraft([...rowsRef.current, ...fresh], receiptNo)
            : null;
          const row = rowFromWire(wire, file.name, previewUrl, uploadedKey);
          if (dup) {
            // Kept and flagged rather than skipped: a receipt the requester
            // uploaded should never just disappear. The duplicate blocks
            // submission until they remove one of the two rows.
            row.serverAnomaly = {
              kind: 'duplicate_in_claim',
              message: `Receipt No. "${receiptNo}" is already in ${dup.where}.`,
            };
            flaggedCount += 1;
          }
          fresh.push(row);
        }
        for (const wire of result.flagged) {
          const row = rowFromWire(wire, file.name, previewUrl, uploadedKey);
          // Only findings the browser cannot reach on its own are pinned to the
          // row. Everything else — an out-of-period date, a missing amount, a
          // currency with no rate — is re-derived live from the row, so it
          // clears the moment it is corrected. Pinning those was what left a
          // fixed date still showing as a finding: the live check passed while
          // a stale copy of the original complaint sat on the row forever.
          row.serverAnomaly = wire.anomalies.find((a) => STICKY_ANOMALIES.has(a.kind)) ?? null;
          fresh.push(row);
          flaggedCount += 1;
        }
        if (fresh.length > 0) dispatch({ type: 'addRows', rows: fresh });

        const key = uploadedKey;
        const pending: PendingConfirm[] = result.needsConfirmation.map((wire) => {
          confirmSeq.current += 1;
          return {
            key: `c${confirmSeq.current}`,
            dateOptions: wire.dateOptions,
            company: wire.company ?? '',
            receipt: wire.receipt ?? '',
            previewUrl,
            wire,
            sourceName: file.name,
            imageKey: key,
          };
        });
        if (pending.length > 0) setPendingConfirms((prev) => [...prev, ...pending]);

        const parts: string[] = [];
        const clean = fresh.length - flaggedCount;
        if (clean > 0) {
          parts.push(`${clean} receipt${clean > 1 ? 's' : ''} added`);
        }
        if (flaggedCount > 0) {
          parts.push(
            `${flaggedCount} added but flagged — fix the highlighted row${flaggedCount > 1 ? 's' : ''} before submitting`,
          );
        }
        if (pending.length > 0) {
          parts.push(
            `${pending.length} receipt${pending.length > 1 ? 's need' : ' needs'} a date confirmation`,
          );
        }

        if (prepared.converted) {
          parts.push('iPhone photo converted to JPEG so it could be read');
        }
        if (prepared.optimized) {
          parts.push('receipt optimized to reduce upload size while keeping it readable');
        }

        const anythingUsed = fresh.length > 0 || pending.length > 0;
        const status: QueueStatus = flaggedCount > 0 || !anythingUsed ? 'warn' : 'ok';
        setQueueItem(qid, {
          status,
          message: parts.join('. ') || 'nothing could be read from this file',
        });
      } catch (err) {
        // Dropping the file here would lose the requester's work. A blank row
        // (keeping the upload, if it got that far) lets them type it in.
        dispatch({
          type: 'addRows',
          rows: [makeRow({ sourceName: file.name, previewUrl, imageKey: uploadedKey })],
        });
        setQueueItem(qid, {
          status: 'error',
          message: `couldn't be read automatically (${errorMessage(err)}) — a blank row was added for you to fill in manually`,
        });
      }
    },
    [setQueueItem],
  );

  const onFiles = useCallback(
    (files: readonly File[]) => {
      for (const file of files) void processFile(file);
    },
    [processFile],
  );

  const dismissQueueItem = useCallback((id: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  /* ---------------- Ambiguous dates ---------------- */

  const head = pendingConfirms[0] ?? null;

  const onChooseDate = useCallback(
    (choice: DateChoice) => {
      const pending = pendingConfirms[0];
      if (!pending) return;
      const date =
        choice === 'original' ? pending.dateOptions.original : pending.dateOptions.swapped;

      if (matchesPeriod(date, state.period)) {
        dispatch({
          type: 'addRows',
          rows: [
            rowFromWire(pending.wire, pending.sourceName, pending.previewUrl, pending.imageKey, date),
          ],
        });
        toast('Receipt added to the claim.', 'ok');
      } else {
        toast(
          `You confirmed this receipt is dated ${date}, which falls outside ${periodLabel(state.period)}. It was not added to this claim.`,
          'err',
        );
      }
      setPendingConfirms((prev) => prev.slice(1));
    },
    [pendingConfirms, state.period, toast],
  );

  const onSkipDate = useCallback(() => {
    const pending = pendingConfirms[0];
    if (!pending) return;
    toast(
      `The date on ${pending.receipt || pending.company || 'that receipt'} was not confirmed, so it was not added to this claim.`,
      'info',
    );
    setPendingConfirms((prev) => prev.slice(1));
  }, [pendingConfirms, toast]);

  /* ---------------- Submit ---------------- */

  /**
   * Every unresolved problem on the claim, per row.
   *
   * Deliberately a full list rather than the first failure: someone fixing one
   * row at a time, learning of the next problem only after correcting the last,
   * has no idea how far from done they are. Nothing here is a surprise Finance
   * should be the first to find.
   */
  const anomaliesByRow = useMemo(
    () => outstandingFindings(state.rows, state.period, rates),
    [rates, state.period, state.rows],
  );

  /** One stable id per finding, so a fixed one can be told from a new one. */
  const hasMissingRate = anomaliesByRow.some(({ items }) =>
    items.some((a) => a.kind === 'missing_fx_rate'),
  );

  const anomalyKeys = useMemo(
    () => anomaliesByRow.flatMap(({ row, items }) => items.map((a) => `${row.id}:${a.kind}`)),
    [anomaliesByRow],
  );

  /**
   * Findings that were outstanding at the last re-check and are not any more.
   *
   * Shown rather than merely dropped: someone who fixed three rows and pressed
   * re-check should be told three things were fixed. A panel that just gets
   * shorter leaves them guessing whether it even re-read their edits.
   */
  const resolvedSinceCheck = useMemo(() => {
    if (!lastCheck) return [] as string[];
    const live = new Set(anomalyKeys);
    return lastCheck.outstanding.filter((key) => !live.has(key));
  }, [anomalyKeys, lastCheck]);

  /**
   * Re-run every check against the rows as they stand now.
   *
   * Most findings already clear on their own — they are derived live from the
   * row, so correcting a date removes its finding as you type. Two cannot:
   * whether a receipt number is used on someone else's claim, and whether
   * Finance has since added a missing exchange rate. Both need the server, so
   * this asks it, and then reports what changed.
   */
  const recheck = useCallback(async (): Promise<void> => {
    setRechecking(true);
    const before = anomalyKeys;
    try {
      const editing = editingRef.current;
      const rows = rowsRef.current;

      const [fx] = await Promise.all([
        api.fx.list<{ rates: Array<{ currency: string; rate: number }> }>(),
        // Every receipt number re-checked at once, not just the flagged ones: a
        // row can become a duplicate because someone else submitted in the
        // meantime, and that row carries no finding to re-check.
        Promise.all(
          rows.map(async (row) => {
            // A pinned finding the browser can now re-derive is dropped. This
            // also repairs rows saved as drafts before that rule existed.
            if (row.serverAnomaly && !STICKY_ANOMALIES.has(row.serverAnomaly.kind)) {
              dispatch({ type: 'updateRow', id: row.id, patch: { serverAnomaly: null } });
            }
            const value = row.receipt.trim();
            if (!value) {
              // Guarded: an unconditional dispatch here would write on every
              // pass and, with the automatic re-check below, re-trigger itself.
              if (row.serverAnomaly?.kind === 'duplicate_elsewhere') {
                dispatch({ type: 'updateRow', id: row.id, patch: { serverAnomaly: null } });
              }
              return;
            }
            const result = await api.claims.checkReceiptDuplicate<DuplicateCheckResult>({
              receiptNo: value,
              ...(editing ? { excludeClaimId: editing } : {}),
            });
            const message = `Receipt No. "${value}" is already used in ${result.where ?? 'another claim'}.`;
            setWarnings((prev) =>
              result.duplicate ? { ...prev, [row.id]: message } : without(prev, row.id),
            );
            // Only this kind is cleared. An 'unreadable' finding is about the
            // file itself and no amount of re-checking makes it readable.
            if (result.duplicate) {
              dispatch({
                type: 'updateRow',
                id: row.id,
                patch: { serverAnomaly: { kind: 'duplicate_elsewhere', message } },
              });
            } else if (row.serverAnomaly?.kind === 'duplicate_elsewhere') {
              dispatch({ type: 'updateRow', id: row.id, patch: { serverAnomaly: null } });
            }
          }),
        ),
      ]);

      const fresh = Object.fromEntries(fx.rates.map((r) => [r.currency, r.rate]));
      setRates(fresh);

      // A row whose rate has appeared is converted now, so the requester does
      // not have to retype anything for the fix to take effect.
      for (const row of rows) {
        const code = (row.currency || 'PHP').toUpperCase();
        if (code === 'PHP') continue;
        const rate = fresh[code];
        const original = Number(row.originalAmount);
        if (!rate || !Number.isFinite(original) || original <= 0) continue;
        const converted = convertToHome(original, rate);
        if (row.fxRate === rate && Number(row.amount) === converted) continue;
        dispatch({
          type: 'updateRow',
          id: row.id,
          patch: { fxRate: rate, amount: String(converted) },
        });
      }

      setLastCheck({ at: Date.now(), outstanding: before });
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setRechecking(false);
    }
  }, [anomalyKeys, toast]);

  // Held in a ref so the effects below depend on what actually changed rather
  // than on the identity of a callback that is rebuilt on every finding.
  const recheckRef = useRef(recheck);
  useEffect(() => {
    recheckRef.current = recheck;
  }, [recheck]);

  /**
   * What the server is being asked about. Amount is excluded on purpose: the
   * re-check writes converted amounts back, and including it here would make
   * the effect below retrigger on its own output.
   */
  const serverCheckSignature = state.rows
    .map((r) => `${r.id}|${r.date}|${r.receipt.trim()}|${r.currency}|${r.originalAmount}`)
    .join(';');

  /**
   * Re-check on its own, shortly after editing stops.
   *
   * Findings derived in the browser already clear as you type. These two do not
   * — a receipt number used on someone else's claim, and a rate only Finance
   * can add — and asking someone to press a button to find out that their fix
   * worked is asking them to do the system's job. The button remains for
   * impatience and for the Finance case, where nothing on this form changes.
   */
  useEffect(() => {
    if (state.rows.length === 0) return undefined;
    const timer = setTimeout(() => void recheckRef.current(), AUTO_RECHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [serverCheckSignature, state.rows.length]);

  /**
   * A missing rate is fixed on a different screen, by a different person, so no
   * edit here will ever signal it. Coming back to the tab is the moment worth
   * re-reading it — and it costs one request.
   */
  useEffect(() => {
    if (!hasMissingRate) return undefined;
    const onFocus = (): void => void recheckRef.current();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [hasMissingRate]);

  const firstProblem = useCallback((): string | null => {
    if (state.rows.length === 0) return 'Add at least one expense before submitting.';

    const invalidPeriod = validatePeriod(state.period);
    if (invalidPeriod) return invalidPeriod;

    if (anomaliesByRow.length > 0) {
      const rows = anomaliesByRow.length;
      return `Resolve ${rows === 1 ? 'the flagged row' : `all ${rows} flagged rows`} before submitting — Finance should not be the first to find them.`;
    }
    return null;
  }, [anomaliesByRow, state.period, state.rows.length]);

  const onSubmit = useCallback(async (): Promise<void> => {
    setFormError(null);
    const problem = firstProblem();
    if (problem) {
      setFormError(problem);
      toast(problem, 'err');
      return;
    }

    setSubmitting(true);
    try {
      const rows = state.rows.map(toWireRow);
      const claim = state.editingClaimId
        ? await api.claims.resubmit<Claim>({
            claimId: state.editingClaimId,
            period: state.period,
            rows,
          })
        : await api.claims.create<Claim>({ period: state.period, rows });

      // The draft has served its purpose. A failure here is not worth
      // surfacing — the claim is submitted, which is what the user cares about.
      if (draftId) {
        await api.drafts
          .discardAfterSubmit<unknown>({ draftId, claimId: claim.id })
          .catch(() => undefined);
        setDraftId(null);
      }

      toast(`Claim ${claim.id} ${state.editingClaimId ? 'resubmitted' : 'submitted'}.`, 'ok');
      dispatch({ type: 'reset', state: emptyDraft() });
      navigate('/my-requests');
    } catch (err) {
      // A 4xx here is the workflow talking to the requester — "receipt number
      // already used in EXP-…", "outside the claim period". It is shown as-is.
      const message = errorMessage(err);
      setFormError(message);
      toast(message, 'err');
    } finally {
      setSubmitting(false);
    }
  }, [draftId, firstProblem, navigate, state.editingClaimId, state.period, state.rows, toast]);

  /* ---------------- Drafts ---------------- */

  /**
   * Save without submitting.
   *
   * Deliberately does not run the submit validation: a draft is allowed to be
   * incomplete, and refusing to save half-finished work is the fastest way to
   * make people stop trusting the button.
   */
  const saveDraft = useCallback(async (): Promise<void> => {
    setSavingDraft(true);
    setFormError(null);
    try {
      const result = await api.drafts.save<{ id: string }>({
        ...(draftId ? { draftId } : {}),
        period: state.period,
        rows: state.rows.map(toWireRow),
      });
      setDraftId(result.id);
      setDraftSavedAt(Date.now());
      toast('Draft saved. You can come back to it later.', 'ok');
    } catch (err) {
      const message = errorMessage(err);
      setFormError(message);
      toast(message, 'err');
    } finally {
      setSavingDraft(false);
    }
  }, [draftId, state.period, state.rows, toast]);

  const deleteDraft = useCallback(async (): Promise<void> => {
    if (!draftId) return;
    try {
      await api.drafts.delete<unknown>({ draftId });
      setDraftId(null);
      setDraftSavedAt(null);
      dispatch({ type: 'reset', state: emptyDraft() });
      toast('Draft deleted.', 'ok');
      navigate('/my-requests');
    } catch (err) {
      toast(errorMessage(err), 'err');
    }
  }, [draftId, navigate, toast]);

  // Load a saved draft when arriving with ?draft=…
  useEffect(() => {
    const id = searchParams.get('draft');
    if (!id) return;
    let alive = true;
    void (async () => {
      setLoadingClaim(true);
      try {
        const d = await api.drafts.get<{ id: string; period: string; rows: unknown[] }>({
          draftId: id,
        });
        if (!alive) return;
        dispatch({
          type: 'reset',
          state: {
            ...emptyDraft(),
            period: d.period,
            rows: (d.rows as DraftRow[]).map((r) => ({ ...makeRow(), ...r })),
          },
        });
        setDraftId(d.id);
      } catch (err) {
        if (alive) setLoadError(errorMessage(err));
      } finally {
        if (alive) setLoadingClaim(false);
      }
    })();
    return () => {
      alive = false;
    };
    // Only on first mount for a given draft id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('draft')]);

  /* ---------------- Render ---------------- */

  const totals = useMemo(() => draftTotals(state.rows), [state.rows]);

  const routing = useMemo((): string => {
    if (!preview) return 'Loading approval routing…';
    if (preview.steps.length === 0) {
      return preview.executiveRoute
        ? 'Executive route — goes straight to Finance Review (no Manager/Lead approval)'
        : 'Goes straight to Finance Review';
    }
    return preview.steps.map((s) => `${s.role.replace(' By', '')}: ${s.name}`).join('  →  ');
  }, [preview]);

  if (loadingClaim) {
    return <LoadingBlock label="Loading the claim you are resubmitting…" />;
  }

  return (
    <>
      {loadError ? <div className="auth-error">{loadError}</div> : null}

      {state.editingClaimId && disapprovalReason ? (
        <div className="cf-disapproval" role="alert">
          <h3>Claim {state.editingClaimId} was disapproved</h3>
          <p>{disapprovalReason}</p>
          <span>Fix the items below and resubmit — the claim keeps its original request ID.</span>
        </div>
      ) : null}

      <ReceiptDropzone onFiles={onFiles} disabled={submitting} />
      <UploadQueue items={queue} onDismiss={dismissQueueItem} />

      <div className="cf-card">
        <div className="cf-header">
          <div className="company-block">
            <div className="company-name">GREAT DEALS E-COMMERCE CORP.</div>
            <div className="company-addr">
              2nd Floor Bookman Building, #373 Quezon Avenue St., Brgy. Lourdes, Quezon City, 1114
              <br />
              (02) 251-1258 • TIN: 009-717-682-000
            </div>
          </div>
          <div className="cf-title">
            <h2>EXPENSE CLAIM FORM</h2>
            <span className="rid">
              {state.editingClaimId
                ? `Resubmitting ${state.editingClaimId}`
                : 'New request — ID assigned on submit'}
            </span>
            <span className="badge2">{periodLabel(state.period).toUpperCase()}</span>
          </div>
        </div>

        <div className="cf-meta">
          <div className="c">
            <label htmlFor="cfEmployeeName">Employee Name</label>
            <input id="cfEmployeeName" value={user.name} disabled />
          </div>
          <div className="c">
            <label htmlFor="cfEmployeeId">Employee ID</label>
            <input id="cfEmployeeId" value={user.empId} disabled />
          </div>
          <div className="c">
            <label htmlFor="cfDepartment">Department</label>
            <input id="cfDepartment" value={user.department} disabled />
          </div>
          <div className="c">
            <label htmlFor="cfPeriod">Date Covered</label>
            {/* A list, not a month picker. Only two periods are ever claimable
                (this month and last), so offering a free calendar invites
                someone to pick March, fill in ten receipts and be refused at
                submit. The options come from the same function the server
                validates with. */}
            <select
              id="cfPeriod"
              value={state.period}
              onChange={(e) => onPeriodChange(e.target.value)}
            >
              {periodOptions.map((p) => (
                <option key={p.value} value={p.value} disabled={p.closed}>
                  {periodLabel(p.value)}
                  {p.closed ? ' — closed' : ''}
                </option>
              ))}
            </select>
            <p className="hint">
              Receipts must be claimed within {CLAIMABLE_MONTHS} months of being issued.
            </p>
          </div>
          <div className="c" style={{ gridColumn: 'span 4' }}>
            <label htmlFor="cfRouting">Approval Routing (auto, from Organization Chart)</label>
            <input id="cfRouting" value={routing} disabled />
          </div>
        </div>

        <DraftTable
          rows={state.rows}
          categories={categories}
          warnings={warnings}
          onField={onField}
          onDateCommit={onDateCommit}
          onReceiptCommit={onReceiptCommit}
          onDelete={onDeleteRow}
        />

        <button type="button" className="btn btn-ghost btn-sm cf-addrow" onClick={onAddRow}>
          ＋ Add Row Manually
        </button>

        {mySignature === null ? (
          <p className="hint" style={{ marginTop: 18 }}>
            No signature saved yet — your name alone will appear on this claim.{' '}
            <Link to="/profile">Add one in your profile</Link> and it will show here and on every
            claim you submit.
          </p>
        ) : null}

        <div className="cf-step-grid" style={{ marginTop: 22 }}>
          <div className="cf-step">
            <div className="role">Prepared By</div>
            <div className="sig">
              {/* Shown from the moment the draft is started, so what will be
                  signed is visible before submitting rather than discovered
                  afterwards on the submitted claim. */}
              <SignatureMark src={mySignature} name={user.name} />
              {user.name}
            </div>
            <div className="dt">Today</div>
          </div>
          {preview?.steps.map((step) => (
            <div className="cf-step" key={`${step.role}-${step.userId}`}>
              <div className="role">{step.role}</div>
              <div className="sig">&nbsp;</div>
              <div className="dt">Pending</div>
            </div>
          ))}
        </div>
      </div>

      {anomaliesByRow.length > 0 ? (
        <div className="anomaly-panel" role="alert">
          <div className="anomaly-head">
            <h4>
              {anomaliesByRow.length === 1
                ? '1 row needs attention before you can submit'
                : `${anomaliesByRow.length} rows need attention before you can submit`}
            </h4>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void recheck()}
              disabled={rechecking || submitting}
              title="Re-read every row now. This also happens on its own a moment after you stop editing."
            >
              {rechecking ? 'Re-checking…' : 'Re-check now'}
            </button>
          </div>
          <p className="hint">
            These receipts are kept — nothing was discarded. Correct or remove each one; Finance
            should not be the first to find them. This list re-checks itself as you edit — including
            with the server, for receipt numbers and exchange rates — so a corrected row clears on
            its own.
          </p>
          {resolvedSinceCheck.length > 0 ? (
            <p className="anomaly-resolved">
              ✓ {resolvedSinceCheck.length} {resolvedSinceCheck.length === 1 ? 'finding' : 'findings'}{' '}
              resolved since the last check. {anomaliesByRow.length}{' '}
              {anomaliesByRow.length === 1 ? 'row' : 'rows'} still outstanding.
            </p>
          ) : lastCheck ? (
            <p className="anomaly-resolved anomaly-nochange">
              Last checked {fmtDateTime(lastCheck.at)} — nothing has been resolved yet.
            </p>
          ) : null}
          <ul>
            {anomaliesByRow.map(({ row, line, items }) => (
              <li key={row.id}>
                <b>
                  Row {line}
                  {row.company ? ` — ${row.company}` : ''}
                  {row.sourceName ? ` (${row.sourceName})` : ''}
                </b>
                <ul>
                  {items.map((a) => (
                    <li key={a.kind}>{a.message}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      ) : lastCheck ? (
        // Only shown once a check has actually run. A green all-clear on a form
        // nobody has checked yet would be reassurance the system has not earned.
        <div className="anomaly-panel anomaly-clear">
          <h4>✓ All findings resolved</h4>
          <p className="hint">
            Every row passed re-check at {fmtDateTime(lastCheck.at)}. This claim is ready to submit.
          </p>
        </div>
      ) : null}

      {formError ? <div className="auth-error">{formError}</div> : null}

      {/* Resubmission edits an existing claim, so there is no draft to keep. */}
      {!state.editingClaimId ? (
        <div className="draft-bar">
          <span>
            {draftId ? 'Working from a saved draft.' : 'Not finished? Save it and come back later.'}
          </span>
          {draftSavedAt ? <span className="draft-saved">Saved</span> : null}
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void saveDraft()}
            disabled={savingDraft || submitting}
          >
            {savingDraft ? 'Saving…' : draftId ? 'Update draft' : 'Save as draft'}
          </button>
          {draftId ? (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => void deleteDraft()}
              disabled={savingDraft || submitting}
            >
              Delete draft
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="cf-card cf-submitbar">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void onSubmit()}
          // Disabled while anything is unresolved, rather than letting the
          // click fail: the panel above already says what is wrong, and a
          // button that looks available but never works is worse than one that
          // plainly is not.
          disabled={submitting || anomaliesByRow.length > 0}
          title={
            anomaliesByRow.length > 0
              ? 'Resolve the flagged rows above before submitting'
              : undefined
          }
        >
          {submitting
            ? 'Submitting…'
            : anomaliesByRow.length > 0
              ? `${anomaliesByRow.length} row${anomaliesByRow.length > 1 ? 's' : ''} need attention`
              : state.editingClaimId
                ? 'Update & Resubmit'
                : 'Submit Expense Claim'}
        </button>
        <span className="cf-total">
          Total claimed: <b>{money(totals.total)}</b>
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
          Routing follows the Organization Chart — no need to pick approvers manually.
        </span>
      </div>

      {head ? (
        <DateConfirmModal
          key={head.key}
          pending={head}
          period={state.period}
          onChoose={onChooseDate}
          onSkip={onSkipDate}
        />
      ) : null}
    </>
  );
}
